import React, { useState, useRef } from 'react';
import styles from './FeedbackForm.module.scss';
import PropTypes from 'prop-types';
import { useForm } from "react-hook-form";
import classNames from 'classnames';
import { httpPostWithCsrfToken } from '../../utils/Http';
import FormInputField from '../FormInputField/FormInputField';
import FormTextAreaField from '../FormTextAreaField/FormTextAreaField';
import FormPolicyAcceptanceField from '../FormPolicyAcceptanceField/FormPolicyAcceptanceField';
import FormCaptchaField from '../FormCaptchaField/FormCaptchaField';
import Button from '../Button';

const FeedbackForm = ({ policy, sitekey, submitUrl }) => {
    const { register, handleSubmit, formState: { errors } } = useForm();

    const captchaRef = useRef(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [showCaptchaError, setShowCaptchaError] = useState(null);
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const onSubmit = (values) => {
        if (!captchaToken) {
            setShowCaptchaError(true);
            return;
        }

        setIsLoading(true);
        setShowErrorMessage(false);
        setShowCaptchaError(false);

        const data = {
            form: values,
            captchaToken: captchaToken
        };

        httpPostWithCsrfToken(
            submitUrl,
            data,
        ).then(async (res) => {
            if (res && res.success) {
                setShowSuccess(true);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        }).catch(() => {
            setIsLoading(false);
            setShowErrorMessage(true);
            setCaptchaToken(null);
            captchaRef.current.reset();
        });
    };

    if (showSuccess) {
        return (
            <div className={styles['FeedbackForm']}>
                <h2 className={styles['FeedbackForm__Success']}>Thank you!</h2>
            </div>
        );
    }

    return (
        <div className={classNames(
            styles['FeedbackForm'],
            {
                [styles[
                    'FeedbackForm--Loading'
                ]]: isLoading,
            }
        )}>
            <form
                className={styles['FeedbackForm__Form']}
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className={classNames(
                    styles['FeedbackForm__Row'],
                    styles['FeedbackForm__Row--Half']
                )}>
                    <div className={styles['FeedbackForm__Field']}>
                        <FormInputField
                            type="text"
                            name="name"
                            label="Name"
                            placeholder="Name"
                            errors={errors}
                            register={{ ...register("name", { required: false }) }}
                        />
                    </div>
                    <div className={styles['FeedbackForm__Field']}>
                        <FormInputField
                            type="email"
                            name="email"
                            label="E-mail address *"
                            placeholder="E-mail address"
                            errors={errors}
                            register={{ ...register("email", { required: true }) }}
                        />
                    </div>
                </div>

                <div className={styles['FeedbackForm__Row']}>
                    <div className={styles['FeedbackForm__Field']}>
                        <FormTextAreaField
                            name="feedback"
                            label="Message *"
                            errors={errors}
                            register={{ ...register("feedback", { required: true }) }}
                        />
                    </div>
                </div>
                <div className={styles['FeedbackForm__Row']}>
                    <div className={styles['FeedbackForm__Field']}>
                        <FormPolicyAcceptanceField
                            name="policy"
                            label={policy}
                            errors={errors}
                            register={{ ...register("policy", { required: true }) }}
                        />
                    </div>
                </div>

                <div className={styles['FeedbackForm__Row']}>
                    <div className={styles['FeedbackForm__Field']}>
                        <FormCaptchaField
                            sitekey={sitekey}
                            setCaptchaToken={setCaptchaToken}
                            showErrorMessage={(showCaptchaError && !captchaToken)}
                            captchaRef={captchaRef}
                        />
                    </div>
                </div>

                <div className={styles['FeedbackForm__Row']}>
                    <div className={styles['FeedbackForm__Button']}>
                        <Button buttonType="submit" text={"Submit"} />
                    </div>
                    {showErrorMessage && (
                        <p className={styles['FeedbackForm__Error']}>
                            An error occurred while sending the email. Please try again.
                        </p>
                    )}
                </div>
            </form>
        </div>

    );
};

FeedbackForm.propTypes = {
    sitekey: PropTypes.string.isRequired,
    submitUrl: PropTypes.string.isRequired,
};

export default FeedbackForm;
